import type { QueryClient } from "@tanstack/react-query";
import { useSession } from "@/lib/auth/useSession";
import type { MeOrg } from "@/lib/auth/types";

/** One stable empty array, so a signed-out render does not churn consumers. */
const NO_ORGANIZATIONS: MeOrg[] = [];

/**
 * Existing live organization memberships from /v1/me, and nothing else.
 *
 * `/v1/me` already returns exactly this shape, so there is deliberately no
 * mapping step: a second local type for the same three fields is a type that
 * drifts the first time the wire grows a fourth.
 */
export function useWorkspaces() {
  const session = useSession();
  return {
    data: session.data?.orgs ?? NO_ORGANIZATIONS,
    error: session.error,
    isError: session.isError && session.data === undefined,
    isPending: session.isPending,
    refetch: session.refetch,
  };
}

/**
 * Drop every cached read scoped to an organization other than the active one.
 *
 * Two organizations' content must never sit in one cache: these are separate
 * confidential clients, and a stale list rendered for the wrong one is the
 * disclosure this whole boundary exists to prevent.
 *
 * The slug is matched at index 1 SPECIFICALLY, because that is where every
 * organization-scoped key carries it — see `STUDIO_QK` and `ORG_QK`. Scanning
 * the whole key instead would treat a slug as a wildcard: an organization
 * named `review`, `characters` or `me` would match unrelated keys and evict
 * the session itself. Slugs are caller-chosen text, so that is reachable.
 */
export function dropForeignWorkspaceQueries(
  queryClient: QueryClient,
  activeSlug: string,
): void {
  if (activeSlug === "") return;
  queryClient.removeQueries({
    predicate: (query) => {
      const scope = query.queryKey[1];
      return typeof scope === "string" && scope !== activeSlug;
    },
  });
}
